import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit'

export const commentaryCountsFeatureKey = 'commentaryCounts'

export interface CommentaryCountsRedux {
  /**
   * Within-account entity counts, keyed by accountId → a namespaced entity map
   * (`<link type>:<id>` → thread count; absent keys mean 0).
   *
   * Per-account (rather than one flat map) because Helios displays entity rows
   * for many accounts at once — multi-account / sleeve-aggregate sessions,
   * session-less break loads, selected accounts — and some entity keys are NOT
   * account-unique: `asset:<secIdentifier>` is a security id shared across
   * accounts, so a flat map would collide. Keying by account keeps each
   * account's counts isolated; `countForRow` selects the sub-map by the row's
   * own accountId.
   */
  countsByAccount: Record<string, Record<string, number>>
  /**
   * DLITE-4112: per-account TOTAL thread counts, keyed by plain accountId
   * (e.g. `{ '4': 8 }`) — lightweight (one number per account) for the
   * Accounts / My Accounts list columns, which show many rows as they scroll.
   * Accumulates across accounts.
   */
  accountCounts: Record<string, number>
  /**
   * DLITE-5094: accounts whose commentary the user cannot read — denied by the
   * thread-event socket, answered 403 by the counts load, or listed in a
   * bulk response's `failed`. Rendered as "commentary unavailable" in the
   * Comments column. Cleared per account by a later successful load.
   */
  unavailableAccountIds: Record<string, true>
  error: string | null
}

export const commentaryCountsInitialState: CommentaryCountsRedux = {
  countsByAccount: {},
  accountCounts: {},
  unavailableAccountIds: {},
  error: null,
}

/**
 * DLITE-5094: which counts request a `commentaryCountsLoadFailed` came from.
 * `single` = the per-account entity `/counts` GET (`loadCommentaryCounts`),
 * `bulk` = the per-account totals POST (`loadCommentaryAccountCounts`).
 */
export type CommentaryCountsLoadSource = 'single' | 'bulk'

/**
 * DLITE-4112 trigger: fetch per-account totals for the given accounts (the ones
 * currently loaded in an account grid). Handled by the effect, which fetches
 * each and merges the results via `commentaryAccountCountsLoaded`.
 */
export const loadCommentaryAccountCounts = createAction<{
  accountIds: string[]
}>('commentaryCounts/loadAccountCounts')

/**
 * DLITE-5094: a bulk totals request for `accountIds` finished without a
 * loaded or failed outcome (HTTP 404: route not deployed), or its result for
 * these ids was superseded by a newer request for them. No reducer handles
 * it — state and the grids' dedup markers stay as they are, so grids still
 * stop asking — it only tells observers the request is no longer in flight.
 */
export const commentaryAccountCountsSettled = createAction<{
  accountIds: string[]
}>('commentaryCounts/accountCountsSettled')

/**
 * DLITE-5094: a single-account entity `/counts` request for `accountId`
 * finished, but a newer request for the same account was dispatched after
 * it, so its result (loaded or failed) is dropped and only the newer one is
 * applied. No reducer handles it; it only tells observers the request is no
 * longer in flight.
 */
export const commentaryCountsSettled = createAction<{
  accountId: string
}>('commentaryCounts/countsSettled')

export const commentaryCountsSlice = createSlice({
  name: commentaryCountsFeatureKey,
  initialState: commentaryCountsInitialState,
  reducers: {
    // Trigger for the effect; state is updated by commentaryCountsLoaded.
    loadCommentaryCounts: (
      state,
      _action: PayloadAction<{ accountId: string }>,
    ) => {
      state.error = null
    },

    commentaryCountsLoaded: (
      state,
      action: PayloadAction<{
        accountId: string
        counts: Record<string, number>
      }>,
    ) => {
      // Store under the account's own sub-map. Per-account keying means a
      // response can never overwrite a different account's counts, so no
      // stale-account guard is needed (retires MR-318's flat-map guard).
      state.countsByAccount[action.payload.accountId] = action.payload.counts
      clearUnavailable(state, [action.payload.accountId])
      state.error = null
    },

    commentaryCountsLoadFailed: (
      state,
      action: PayloadAction<{
        accountId: string
        error: string
        /** DLITE-5094: true when the failure means the user cannot read this
         *  account's commentary (single 403, bulk 403, bulk `failed`). */
        unavailable?: boolean
        /** DLITE-5094: the denial may be transient (bulk `failed`), so it is
         *  worth retrying with backoff. */
        retryable?: boolean
        /** DLITE-5094: which request failed — the single-account entity
         *  `/counts` GET or the bulk totals POST — so only that surface's
         *  dedup markers are dropped. Absent = unknown (both are dropped). */
        source?: CommentaryCountsLoadSource
      }>,
    ) => {
      state.error = action.payload.error
      if (action.payload.unavailable) {
        markUnavailable(state, [action.payload.accountId])
      }
    },

    /** DLITE-5094: the thread-event socket refused to subscribe these accounts. */
    commentaryAccountsUnavailable: (
      state,
      action: PayloadAction<{ accountIds: string[] }>,
    ) => {
      markUnavailable(state, action.payload.accountIds)
    },

    commentaryAccountCountsLoaded: (
      state,
      action: PayloadAction<{ counts: Record<string, number> }>,
    ) => {
      // Merge (accumulate) so previously-loaded account rows keep their totals
      // as new blocks scroll into view.
      state.accountCounts = { ...state.accountCounts, ...action.payload.counts }
      clearUnavailable(state, Object.keys(action.payload.counts))
    },

    clearCommentaryCounts: state => {
      // Clears the per-account entity maps; the account-list totals
      // (accountCounts) and unavailableAccountIds are independent and persist.
      state.countsByAccount = {}
      state.error = null
    },
  },
})

function markUnavailable(state: CommentaryCountsRedux, accountIds: string[]) {
  if (accountIds.length === 0) return
  const next = { ...(state.unavailableAccountIds ?? {}) }
  for (const id of accountIds) next[id] = true
  state.unavailableAccountIds = next
}

function clearUnavailable(state: CommentaryCountsRedux, accountIds: string[]) {
  const current = state.unavailableAccountIds
  if (!current || !accountIds.some(id => current[id])) return
  const drop = new Set(accountIds)
  const next: Record<string, true> = {}
  for (const id of Object.keys(current)) {
    if (!drop.has(id)) next[id] = true
  }
  state.unavailableAccountIds = next
}

export const {
  loadCommentaryCounts,
  commentaryCountsLoaded,
  commentaryCountsLoadFailed,
  commentaryAccountsUnavailable,
  commentaryAccountCountsLoaded,
  clearCommentaryCounts,
} = commentaryCountsSlice.actions

export const commentaryCountsReducer = commentaryCountsSlice.reducer
